import { HOSTING_WINDOWS } from '@simplelaunch/shared'
import { PALETTES, rgba } from '@simplelaunch/renderer'

const money = (cents: number) => `$${(cents / 100).toFixed(0)}`

/**
 * The landing page.
 *
 * Every pricing button carries its window in the URL, so the choice survives
 * the jump into the builder and a founder never picks the same thing twice.
 *
 * The hero uses the same duotoned NASA photograph the product itself offers as
 * a backdrop, tinted with the same functions -- so the marketing is literally
 * made of the thing being sold rather than a stock image standing in for it.
 */
export function Landing() {
  const juno = PALETTES.juno

  // The page scrim is tuned to guarantee contrast over a whole page of text.
  // A hero has text on one side only, so it can be far lighter and let the
  // photograph actually show: dark where the words are, clear where the
  // shuttle is, with just enough accent to keep it on-brand.
  const heroBackground = [
    `linear-gradient(100deg, ${rgba(juno.bg, 0.95)} 0%, ${rgba(juno.bg, 0.82)} 32%, ${rgba(juno.bg, 0.35)} 62%, transparent 88%)`,
    `linear-gradient(180deg, ${rgba(juno.bg, 0.8)} 0%, transparent 22%, transparent 62%, ${rgba(juno.bg, 0.97)} 100%)`,
    `linear-gradient(${rgba(juno.accent, 0.14)}, ${rgba(juno.accent, 0.04)})`,
    `image-set(url("/bg/launch.avif") type("image/avif"), url("/bg/launch.webp") type("image/webp"))`,
  ].join(',')

  return (
    <div className="lp">
      <header className="lp-nav">
        <a className="lp-brand" href="/">simplelaunch<span>.</span></a>
        <nav>
          <a href="#how">How it works</a>
          <a href="#pricing">Pricing</a>
          <a className="lp-nav-cta" href="/build">Start</a>
        </nav>
      </header>

      <section
        className="lp-hero"
        style={{
          backgroundColor: juno.bg,
          backgroundImage: heroBackground,
          // Only the accent layer blends; the gradients sit on top normally so
          // the darkening stays predictable.
          backgroundBlendMode: 'normal,normal,screen,normal',
        }}
      >
        <div className="lp-hero-inner">
          <p className="lp-eyebrow">For founders who should be building</p>
          <h1>
            Your launch page,<br />live in two minutes.
          </h1>
          <p className="lp-sub">
            A name, one line about it, and a date. Pick a look from a set where
            nothing clashes. Then ship it and get back to work.
          </p>
          <div className="lp-cta-row">
            <a className="lp-cta" href="/build">Build your page</a>
            <a className="lp-ghost" href="#pricing">See pricing</a>
          </div>
          <p className="lp-note">No account. Pay once, not monthly.</p>
        </div>
      </section>

      <section className="lp-section" id="how">
        <h2>Three fields and a look</h2>
        <div className="lp-steps">
          <div className="lp-step">
            <b>1</b>
            <h3>Say what you're building</h3>
            <p>Your startup name, one line about it, and the date you're launching.</p>
          </div>
          <div className="lp-step">
            <b>2</b>
            <h3>Pick a look</h3>
            <p>
              Six palettes, four typefaces, six backdrops. Every combination is
              vetted, so none of them look bad.
            </p>
          </div>
          <div className="lp-step">
            <b>3</b>
            <h3>Ship it</h3>
            <p>
              Pay once and your page is live at your own address, counting down,
              collecting emails.
            </p>
          </div>
        </div>
      </section>

      <section className="lp-section" id="pricing">
        <h2>Pay for time, not a subscription</h2>
        <p className="lp-lede">
          You buy hosting for as long as you need it. Nothing renews on its own,
          and you can extend whenever you like.
        </p>

        <div className="lp-prices">
          {HOSTING_WINDOWS.map((w) => (
            <a
              key={w.id}
              className={`lp-price${w.id === 'window_6mo' ? ' lp-price-featured' : ''}`}
              href={`/build?w=${w.id}`}
            >
              {w.id === 'window_6mo' && <span className="lp-badge">Most picked</span>}
              <span className="lp-amount">{money(w.priceCents)}</span>
              <span className="lp-term">{w.label} of hosting</span>
              <ul>
                <li>Your own address</li>
                <li>Countdown to launch</li>
                <li>Email signups, exportable</li>
                <li>Edit any time</li>
              </ul>
              <span className="lp-price-cta">Start with {w.label}</span>
            </a>
          ))}
        </div>
        <p className="lp-note lp-centered">
          One-time payment. Your launch date has to fall inside the window you
          pick — extend later if plans move.
        </p>
      </section>

      <footer className="lp-footer">
        <div>
          <a className="lp-brand" href="/">simplelaunch<span>.</span></a>
          <p>A launch page for your startup, without the afternoon.</p>
        </div>
        <nav>
          <a href="/build">Build a page</a>
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
          <a href="/refunds">Refunds</a>
          <a href="/acceptable-use">Acceptable use</a>
        </nav>
      </footer>
    </div>
  )
}
